import React, {useCallback, useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getAllFilterKeys} from 'components/Filters/utils'
import {useFiltersForm} from 'hooks/useFiltersForm'
import {getIsRtlLanguage} from 'modules/meta/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useElementSize} from '@vio/core/src/_web/utils/browser/hooks/useElementSize'
import {throttle} from '@vio/core/src/utils/throttle'
import {ScrollButton} from '@vio/shared/src/search/Swiper/ScrollButton'

const Wrapper = styled.section`
  padding-right: ${cssTheme.layout.spacing.s600};
  flex: 1;
  overflow-x: auto;
  display: flex;
  justify-content: center;
  flex-direction: column;

  ${linariaMq.desktopLg} {
    flex: 'none';
  }
`

const ScrollWrapper = styled.div`
  width: 100%;
  display: flex;
  overflow: scroll;
  gap: ${cssTheme.layout.spacing.s300};
  /* hide scrollbar */
  -ms-overflow-style: none; /* for Internet Explorer, Edge */
  scrollbar-width: none; /* for Firefox */
  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari and Opera */
  }
`

type ActionType = 'NEXT' | 'PREV'

const allFilterKeys = getAllFilterKeys()

const THROTTLING_INTERVAL = 500 // ms

/**
 * Wrapper used to handle the horizontal scrolling on the filters bar
 */
interface Props {
  children: React.ReactNode
  left?: number
  right?: number
  // Threshold for scroll position
  threshold?: number
  filtersChanged?: boolean
}
export const FiltersScrollWrapper: React.FC<Props> = ({
  children,
  left,
  right,
  threshold = 200,
  filtersChanged = false
}) => {
  const isRtl = useSelector(getIsRtlLanguage)

  const {
    elementRef,
    size: {width: wrapperWidth, el: wrapperElement}
  } = useElementSize<HTMLDivElement>()
  const [scrollableOffset, setScrollableOffset] = useState(0)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [scrollWidth, setScrollWidth] = useState(0)

  const {isMobile} = useDeviceLayout()
  const {getFilters} = useFiltersForm(allFilterKeys)

  const filters = getFilters()

  useEffect(() => {
    if (wrapperElement) {
      const roundedWrapperWidth = Math.ceil(wrapperWidth)
      const newScrollableOffset =
        roundedWrapperWidth > 0
          ? Math.ceil(wrapperElement.scrollWidth) - roundedWrapperWidth
          : 0

      if (scrollableOffset !== newScrollableOffset) {
        setScrollableOffset(Math.round(newScrollableOffset))
      }
    }
  }, [filters, scrollableOffset, wrapperWidth, scrollPosition, wrapperElement])

  useEffect(() => {
    const ref = wrapperElement

    if (ref) {
      setScrollWidth(Math.ceil(ref.scrollWidth))

      const onScroll = throttle(() => {
        setScrollPosition(Math.round(ref.scrollLeft))
      }, THROTTLING_INTERVAL)

      ref.addEventListener('scroll', onScroll)
      return () => ref.removeEventListener('scroll', onScroll)
    }
  }, [wrapperElement])

  const getWidthToScroll = useCallback(
    (direction: ActionType) => {
      const realScrollPosition = isRtl ? -scrollPosition : scrollPosition
      if (direction === 'NEXT') {
        if (scrollWidth >= realScrollPosition + wrapperWidth)
          return Math.ceil(realScrollPosition + wrapperWidth) - threshold
        return scrollableOffset
      }
      if (realScrollPosition + threshold >= wrapperWidth)
        return Math.ceil(realScrollPosition - wrapperWidth + threshold)
      return 0
    },
    [
      isRtl,
      scrollPosition,
      scrollWidth,
      scrollableOffset,
      wrapperWidth,
      threshold
    ]
  )

  const isButtonVisible = useCallback(
    (direction: ActionType): boolean => {
      if (isRtl) {
        if (direction === 'NEXT') return -scrollPosition < scrollableOffset
        return scrollPosition < 0
      }

      if (direction === 'NEXT') return scrollPosition < scrollableOffset
      return scrollPosition > 0
    },
    [isRtl, scrollPosition, scrollableOffset]
  )

  const onArrowClick = useCallback(
    (direction: ActionType) => {
      if (!isButtonVisible(direction)) return
      const widthToScroll = getWidthToScroll(direction)
      wrapperElement.scroll({
        left: (isRtl ? -1 : 1) * widthToScroll,
        behavior: 'smooth'
      })
    },
    [wrapperElement, getWidthToScroll, isRtl, isButtonVisible]
  )

  useEffect(() => {
    if (filtersChanged && wrapperElement) {
      wrapperElement.scroll({
        left: 0,
        behavior: 'smooth'
      })
    }
  }, [filtersChanged, wrapperElement])

  return (
    <Wrapper>
      {!isMobile && (
        <>
          <ScrollButton
            direction="left"
            isVisible={isButtonVisible('PREV')}
            onClick={() => onArrowClick('PREV')}
            left={left}
            isRtl={isRtl}
          />
          <ScrollButton
            direction="right"
            isVisible={isButtonVisible('NEXT')}
            onClick={() => onArrowClick('NEXT')}
            right={right}
            marginRight="s000"
            isRtl={isRtl}
          />
        </>
      )}
      <ScrollWrapper ref={elementRef}>{children}</ScrollWrapper>
    </Wrapper>
  )
}
